import { useEffect, useState } from "react";
import {
  Users,
  Heart,
  Link2,
  Star,
  Calendar,
  TrendingUp,
  Sparkles,
} from "lucide-react";
import client from "../../lib/api";

/**
 * Story Header — the headliner at the top of /admin.
 *
 * One overview pull on mount, then displayed as a wide gilded card.
 * Designed to feel like a serious command-center heading rather than
 * "yet another card" — wider, taller, with a real gold border and a
 * subtle parchment gradient that visually echoes the donation
 * certificate.
 */
export default function StoryHeader() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    client
      .get("/admin/overview")
      .then((r) => {
        if (!cancelled) setData(r.data);
      })
      .catch((e) => {
        if (!cancelled) setError(e?.response?.data?.detail || "טעינה נכשלה");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div
        className="rounded-md border border-gold/30 bg-card/40 p-8 mb-8 animate-pulse"
        data-testid="story-header-loading"
      >
        <div className="h-6 w-1/3 bg-foreground/10 rounded" />
        <div className="h-4 w-1/4 bg-foreground/5 rounded mt-3" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
          <div className="h-24 bg-foreground/5 rounded" />
          <div className="h-24 bg-foreground/5 rounded" />
          <div className="h-24 bg-foreground/5 rounded" />
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div
        className="rounded-md border border-destructive/30 bg-destructive/5 p-6 mb-8 text-sm text-destructive"
        data-testid="story-header-error"
      >
        {error || "טעינה נכשלה"}
      </div>
    );
  }

  const { user_name, hebrew_date, active_status, this_week, top_promoter_this_week } = data;
  const { active_today, active_this_week, inactive_over_week, total } = active_status;

  return (
    <section
      className="
        relative overflow-hidden mb-10
        rounded-md
        border-2 border-gold/40
        bg-gradient-to-br from-gold/[0.12] via-card/90 to-navy/[0.04]
        shadow-[0_12px_36px_-14px_rgba(184,137,59,0.45),0_2px_6px_-2px_rgba(30,42,58,0.08)]
        p-8 md:p-10
      "
      data-testid="story-header"
    >
      {/* Subtle decorative diamond in the top-left corner — a quiet
          callback to the certificate divider, signalling premium. */}
      <div className="absolute top-6 left-6 w-3 h-3 bg-gold/60 rotate-45" aria-hidden />

      {/* Salutation row */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-8">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-gold/80 font-heading mb-2">
            <Sparkles className="inline h-3 w-3 mr-1 -mt-0.5" strokeWidth={1.5} />
            מרכז הפיקוד
          </p>
          <h2
            className="font-heading text-3xl md:text-4xl text-foreground leading-tight"
            data-testid="story-header-greeting"
          >
            שלום, {user_name}
          </h2>
        </div>
        <p
          className="text-sm md:text-base text-muted-foreground flex items-center gap-2"
          data-testid="story-header-date"
        >
          <Calendar className="h-4 w-4 text-gold/70" strokeWidth={1.5} />
          {hebrew_date}
        </p>
      </div>

      {/* Three-up grid: Activity / This week / Star promoter */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* === Active users === */}
        <div
          className="bg-card/70 rounded-md border border-border/60 p-5"
          data-testid="story-header-activity"
        >
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-muted-foreground mb-4">
            <Users className="h-3.5 w-3.5 text-gold" strokeWidth={1.5} />
            פעילות
          </div>
          <ul className="space-y-2.5 text-sm">
            <li className="flex items-center justify-between" data-testid="active-today-row">
              <span className="flex items-center gap-2">
                <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" />
                פעילים היום
              </span>
              <strong className="font-heading text-lg text-foreground">{active_today}</strong>
            </li>
            <li className="flex items-center justify-between" data-testid="active-week-row">
              <span className="flex items-center gap-2">
                <span className="inline-block w-2 h-2 rounded-full bg-amber-500" />
                פעילים השבוע
              </span>
              <strong className="font-heading text-lg text-foreground">{active_this_week}</strong>
            </li>
            <li className="flex items-center justify-between" data-testid="inactive-row">
              <span className="flex items-center gap-2">
                <span className="inline-block w-2 h-2 rounded-full bg-rose-500" />
                לא חזרו מעל שבוע
              </span>
              <strong className="font-heading text-lg text-foreground">{inactive_over_week}</strong>
            </li>
            <li className="flex items-center justify-between pt-2 mt-2 border-t border-border/40">
              <span className="text-xs text-muted-foreground">סה"כ משתמשים</span>
              <strong className="font-heading text-base text-foreground">{total}</strong>
            </li>
          </ul>
        </div>

        {/* === This week === */}
        <div
          className="bg-card/70 rounded-md border border-border/60 p-5"
          data-testid="story-header-week"
        >
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-muted-foreground mb-4">
            <TrendingUp className="h-3.5 w-3.5 text-gold" strokeWidth={1.5} />
            השבוע בתוריא
          </div>
          <ul className="space-y-3 text-sm">
            <li className="flex items-center gap-3" data-testid="week-signups">
              <Users className="h-4 w-4 text-muted-foreground/70" strokeWidth={1.5} />
              <span className="font-heading text-base text-foreground">
                {this_week.new_signups}
              </span>
              <span className="text-muted-foreground">משתמשים חדשים</span>
            </li>
            <li className="flex items-center gap-3" data-testid="week-donations">
              <Heart className="h-4 w-4 text-muted-foreground/70" strokeWidth={1.5} />
              <span className="font-heading text-base text-foreground">
                {this_week.donations_total_ils > 0 && `₪${this_week.donations_total_ils}`}
                {this_week.donations_total_usd > 0 &&
                  (this_week.donations_total_ils > 0 ? " · " : "") +
                    `$${this_week.donations_total_usd}`}
                {this_week.donations_total_ils === 0 &&
                  this_week.donations_total_usd === 0 &&
                  "₪0"}
              </span>
              <span className="text-muted-foreground">
                ({this_week.donations_count} {this_week.donations_count === 1 ? "תרומה" : "תרומות"})
              </span>
            </li>
            <li className="flex items-center gap-3" data-testid="week-clicks">
              <Link2 className="h-4 w-4 text-muted-foreground/70" strokeWidth={1.5} />
              <span className="font-heading text-base text-foreground">
                {this_week.referral_clicks}
              </span>
              <span className="text-muted-foreground">לחיצות הפניה</span>
            </li>
          </ul>
        </div>

        {/* === Star promoter === */}
        <div
          className="bg-gradient-to-br from-gold/10 to-card/70 rounded-md border border-gold/30 p-5"
          data-testid="story-header-promoter"
        >
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-gold mb-4">
            <Star className="h-3.5 w-3.5" strokeWidth={1.5} />
            המפיץ הכוכב
          </div>
          {top_promoter_this_week ? (
            <div>
              <p
                className="font-heading text-xl text-foreground mb-1"
                data-testid="promoter-name"
              >
                {top_promoter_this_week.name}
              </p>
              <p className="text-xs text-muted-foreground mb-3">
                {top_promoter_this_week.email}
              </p>
              <p className="text-sm">
                הביא{" "}
                <strong className="font-heading text-lg text-gold">
                  {top_promoter_this_week.signups_brought}
                </strong>{" "}
                {top_promoter_this_week.signups_brought === 1 ? "משתמש" : "משתמשים"} השבוע
              </p>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground italic mt-4">
              עוד אין הפניות פעילות השבוע.<br />
              <span className="text-xs">שתף קישור הפניה כדי להתחיל.</span>
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
